/**
 * Touch gesture classification. Gestures only fire after a deliberate
 * horizontal drag so vertical scrolling and taps keep working; the same
 * actions are always available as visible buttons.
 */
export type SwipeDirection = 'left' | 'right';

export interface SwipeOptions {
  /** Minimum horizontal travel in CSS pixels. */
  threshold?: number;
  /** Horizontal travel must exceed vertical travel by this factor. */
  dominance?: number;
}

export function classifySwipe(dx: number, dy: number, options: SwipeOptions = {}): SwipeDirection | null {
  const threshold = options.threshold ?? 56;
  const dominance = options.dominance ?? 1.4;
  if (Math.abs(dx) < threshold) return null;
  if (Math.abs(dx) < Math.abs(dy) * dominance) return null;
  return dx < 0 ? 'left' : 'right';
}

export function classifySwipePoints(
  start: { x: number; y: number },
  end: { x: number; y: number },
  options?: SwipeOptions,
): SwipeDirection | null {
  return classifySwipe(end.x - start.x, end.y - start.y, options);
}

export interface DragOptions {
  /** Minimum travel before a drag counts as horizontal. */
  minTravel?: number;
  /** Horizontal travel must exceed vertical travel by this factor. */
  dominance?: number;
}

/**
 * True once a touch drag is clearly horizontal. Until then the gesture keeps
 * following the browser's vertical scroll and pinch-zoom.
 */
export function isHorizontalDrag(dx: number, dy: number, options: DragOptions = {}): boolean {
  const minTravel = options.minTravel ?? 8;
  const dominance = options.dominance ?? 1.2;
  return Math.abs(dx) >= minTravel && Math.abs(dx) > Math.abs(dy) * dominance;
}

/** Reply-swipe travel in CSS pixels, with light resistance so it feels elastic. */
export function replyDragOffset(dx: number, limit = 96): number {
  if (dx >= 0) return 0;
  const travel = Math.min(-dx, limit);
  return -Math.round(travel * (1 - travel / (limit * 4)));
}

/** Back-swipe travel: follows the finger, then damps past a soft limit. */
export function backDragOffset(dx: number, width: number): number {
  if (dx <= 0) return 0;
  const soft = Math.max(48, width * 0.35);
  if (dx <= soft) return Math.round(dx);
  return Math.round(soft + (dx - soft) * 0.35);
}

/** 0..1 progress toward the reply threshold, used to animate the affordance. */
export function swipeProgress(dx: number, threshold = 56): number {
  if (dx >= 0) return 0;
  return Math.min(1, Math.round((-dx / threshold) * 100) / 100);
}

/** Right swipe returns to the chat list, matching the visible back button. */
export function isBackSwipe(dx: number, dy: number, options?: SwipeOptions): boolean {
  return classifySwipe(dx, dy, options) === 'right';
}

/** Left swipe on a message starts a reply, matching the visible reply button. */
export function isReplySwipe(dx: number, dy: number, options?: SwipeOptions): boolean {
  return classifySwipe(dx, dy, options) === 'left';
}
